/**
 * Le micro du widget (`POST /runtime/transcrire`, core-ai D46).
 *
 * Clic pour parler, clic pour envoyer, 30 s au plus. L'enregistrement reste en
 * mémoire le temps d'être envoyé, puis il est oublié : rien n'est écrit dans le
 * navigateur du visiteur.
 */

/** Au-delà, l'écoute s'arrête seule : une question tient en moins de 30 s. */
export const DUREE_MAX_MS = 30_000;

/** Les formats par ordre de préférence : opus est léger, mp4 est celui de Safari. */
const FORMATS = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

/** Le premier format que sait produire le navigateur, ou rien (son défaut). */
export function choisirFormat(estSupporte: (type: string) => boolean): string | undefined {
  return FORMATS.find((type) => estSupporte(type));
}

export class Micro {
  private enregistreur: MediaRecorder | null = null;
  private flux: MediaStream | null = null;
  private morceaux: Blob[] = [];
  private minuterie = 0;

  /** Le navigateur sait-il enregistrer ? (contexte sécurisé, API présentes) */
  static disponible(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.isSecureContext &&
      Boolean(navigator.mediaDevices?.getUserMedia) &&
      typeof MediaRecorder !== 'undefined'
    );
  }

  /**
   * Ouvre le micro et commence à enregistrer. Le navigateur demande la
   * permission la première fois ; un refus lève une erreur.
   *
   * `surDureeMax` : appelée si le visiteur parle 30 s sans cliquer.
   */
  async demarrer(surDureeMax: () => void): Promise<void> {
    this.flux = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });
    const format = choisirFormat((type) => MediaRecorder.isTypeSupported(type));
    this.enregistreur = new MediaRecorder(this.flux, format ? { mimeType: format } : undefined);
    this.morceaux = [];
    this.enregistreur.ondataavailable = (e) => {
      if (e.data.size) this.morceaux.push(e.data);
    };
    this.enregistreur.start();
    this.minuterie = window.setTimeout(surDureeMax, DUREE_MAX_MS);
  }

  /** Arrête l'écoute et rend l'enregistrement (null s'il est vide). */
  arreter(): Promise<Blob | null> {
    window.clearTimeout(this.minuterie);
    const enregistreur = this.enregistreur;
    if (!enregistreur || enregistreur.state === 'inactive') {
      this.liberer();
      return Promise.resolve(null);
    }
    return new Promise((resoudre) => {
      enregistreur.onstop = () => {
        const type = enregistreur.mimeType || this.morceaux[0]?.type || 'audio/webm';
        const enregistrement = new Blob(this.morceaux, { type });
        this.morceaux = [];
        this.liberer();
        resoudre(enregistrement.size ? enregistrement : null);
      };
      enregistreur.stop();
    });
  }

  /**
   * Rend le micro au système : sans cela, le voyant d'enregistrement du
   * navigateur reste allumé, et le visiteur croit qu'on l'écoute encore.
   */
  private liberer(): void {
    this.flux?.getTracks().forEach((piste) => piste.stop());
    this.flux = null;
    this.enregistreur = null;
  }
}
